import React, { Fragment, useState, useEffect } from 'react';
import classnames from 'classnames';
import {
  AppBar,
  AppBarSection,
  AppBarContext,
  Button,
  Checkbox,
  InputBox,
  Icons,
  RadioButton,
  Skeleton,
} from '@densityco/ui';
import HorizontalForm from '../HorizontalForm';
import colorVariables from '@densityco/ui/variables/colors.json';
import styles from './styles.scss';
import Popup, { PopupProps } from '../Popup';

import {
  FilterOperator,
  FilterDefinition,
  FilterOperatorComponentProps,
  FilterValue,
  Filter,
} from '../../types/filters';
import { Choice } from '../../types/choice';

// ------------------------------------------------------------------------------
// Filter Operator Inputs
// These are presets that can be used for a filter operator's "component" property.
// ------------------------------------------------------------------------------

export const FilterOperatorInputText: React.FunctionComponent<FilterOperatorComponentProps<
  string
>> = ({ value, setValue, definition, operator }) => (
  <InputBox
    type="text"
    value={value}
    onChange={e => setValue(e.target.value)}
    invalid={operator ? !operator.isValueValid(value) : false}
    width="100%"
  />
);

// Eventually it might be good to make this an actual date picker
export const FilterOperatorInputDate: React.FunctionComponent<FilterOperatorComponentProps<
  string
>> = ({ value, setValue, definition, operator }) => (
  <InputBox
    type="text"
    value={value}
    onChange={e => setValue(e.target.value)}
    invalid={operator ? !operator.isValueValid(value) : false}
    placeholder="YYYY-MM-DD"
    width="100%"
  />
);

export const FilterOperatorInputPercent: React.FunctionComponent<FilterOperatorComponentProps<
  string
>> = ({ value, setValue, definition, operator }) => (
  <InputBox
    type="text"
    value={value}
    onChange={e => setValue(e.target.value)}
    invalid={operator ? !operator.isValueValid(value) : false}
    rightIcon={<strong>%</strong>}
    width="100%"
  />
);

type FilterOperatorInputChoicesProps = FilterOperatorComponentProps<
  Choice['id']
> & {
  choices: Array<Choice>;
};

export const FilterOperatorInputChoices: React.FunctionComponent<FilterOperatorInputChoicesProps> = ({
  choices,
  value,
  setValue,
  definition,
  operator,
}) => (
  <InputBox
    type="select"
    choices={choices}
    placeholder={choices.length > 0 ? 'Please pick a choice.' : 'No choices available'}
    disabled={choices.length === 0}
    value={value}
    onChange={choice => setValue(choice.id)}
    invalid={operator ? !operator.isValueValid(value) : false}
    width="100%"
    menuMaxHeight={250}
  />
);

type FilterOperatorInputMultiChoicesProps = FilterOperatorComponentProps<
  Array<Choice['id']>
> & {
  choices: Array<Choice>;
};

export const FilterOperatorInputMultiChoices: React.FunctionComponent<FilterOperatorInputMultiChoicesProps> = ({
  choices,

  value,
  setValue,
  definition,
  operator,
}) =>
  choices.length === 0 ? (
    <p>No choices.</p>
  ) : (
    <div
      className={classnames(styles.itemListContainer, {
        [styles.invalid]: operator ? !operator.isValueValid(value) : false,
      })}
    >
      <MultipleSelectItemList
        choices={choices}
        value={value}
        onChange={setValue}
        noPadding
      />
    </div>
  );

export const FilterOperatorInputBoolean: React.FunctionComponent<FilterOperatorComponentProps<
  boolean
>> = ({ value, setValue, definition, operator }) => (
  <InputBox
    type="select"
    choices={[
      { id: 'true', label: 'True' },
      { id: 'false', label: 'False' },
    ]}
    menuMaxHeight={100}
    width="100%"
    invalid={operator ? !operator.isValueValid(value) : false}
    value={(() => {
      if (value === true) {
        return 'true';
      } else if (value === false) {
        return 'false';
      } else {
        return null;
      }
    })()}
    onChange={choice => setValue(choice.id === 'true')}
  />
);

type ActionBarSelectTypeToggleProps = {
  isSingleSelect?: boolean;
  onChangeSelectType?: () => void;
}

export const ActionBarSelectTypeToggle: React.FunctionComponent<ActionBarSelectTypeToggleProps> = ({
  isSingleSelect = true,
  onChangeSelectType,
}) => {
  return (
  <Button
    variant="filled"
    onClick={onChangeSelectType}
    data-test-id={isSingleSelect ? "action-bar-edit-button" : "action-bar-done-button"}
  >
    {isSingleSelect ? 'Edit' : 'Done'}
  </Button>
)}

type ActionBarFilterProps = {
  filterDefinitions: Array<FilterDefinition>;
  filters: Array<Filter>;
  onChangeFilters: (filters: Array<Filter>) => void;
  position?: PopupProps['position'];
};

export const ActionBarFilters: React.FunctionComponent<ActionBarFilterProps> = ({
  filterDefinitions,
  filters,
  onChangeFilters,
  position,
}) => {
  const [open, setOpen] = useState(false);
  const [workingFilters, setWorkingFilters] = useState<Array<Partial<Filter>>>(
    filters,
  );

  // When the popup opens, update the working set of filters with the filters passed in via props.
  useEffect(() => {
    if (!open) {
      setWorkingFilters(filters);
    }
  }, [filters]);

  // When the popup closes, sync the updates made in the popup back to the store
  useEffect(() => {
    if (!open && filters !== workingFilters) {
      // Remove any filters that are invalid / not fully filled out
      const validFilters = workingFilters.filter(filter => {
        const filterDefinition = filterDefinitions.find(
          fd => fd.id === filter.definitionId,
        );
        const operator = filterDefinition
          ? filterDefinition.operators.find(o => o.id === filter.operatorId)
          : null;

        return (
          filter.id &&
          filter.definitionId !== null &&
          filter.operatorId !== null &&
          (operator ? operator.isValueValid(filter.value) : true)
        );
      }) as Array<Filter>;

      onChangeFilters(validFilters);
    }
  }, [workingFilters, open]);

  // Number of filters that should be shown in the "filter" button
  const numberOfActiveFilters = open ? workingFilters.length : filters.length;

  return (
    <Popup
      target={
        <Button
          variant={numberOfActiveFilters > 0 ? 'filled' : 'default'}
          leftIcon={<Icons.Filter />}
          disabled={filterDefinitions.length === 0}
        >
          {numberOfActiveFilters === 0
            ? 'Filters'
            : `${numberOfActiveFilters} Filter${numberOfActiveFilters !== 1 ? 's' : ''
            }`}
        </Button>
      }
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      position={position || { top: 44, right: 0 }}
      noPadding
    >
      <div className={styles.actionBarWrapper}>
        {workingFilters.length > 0 ? (
          <Fragment>
            <div className={styles.actionBarContent}>
              {workingFilters.map((filter, index) => {
                const filterDefinition = filterDefinitions.find(
                  i => i.id === filter.definitionId,
                );
                const operator: FilterOperator<
                  FilterValue
                > | null = filterDefinition
                    ? filterDefinition.operators.find(
                      o => o.id === filter.operatorId,
                    ) || null
                    : null;

                const setWorkingFilter = filter => {
                  const filtersCopy = workingFilters.slice();
                  filtersCopy[index] = filter;
                  setWorkingFilters(filtersCopy);
                };

                return (
                  <div key={filter.id} className={styles.actionBarItem}>
                    <div key={filter.id} className={styles.actionBarTopRow}>
                      <div className={styles.actionBarRowPrefix}>
                        {index === 0 ? 'Where' : 'And'}
                      </div>
                      <div className={styles.actionBarRowDefinition}>
                        <InputBox
                          type="select"
                          menuMaxHeight={300}
                          width="100%"
                          placeholder="Choose filter"
                          value={filter.definitionId}
                          choices={filterDefinitions}
                          onChange={(choice: FilterDefinition) => {
                            const filterCopy = {
                              ...filter,
                              definitionId: choice.id,
                            };

                            // Clear out the operator id and state if the newly selected filter
                            // definition does not have the same operator as the previously selected
                            // one.
                            if (
                              !choice.operators.find(
                                i => i.id === filterCopy.operatorId,
                              )
                            ) {
                              // Find the first non-disabled operator in the operator list
                              const validOperator = choice.operators.find(
                                operator => {
                                  return !Boolean(
                                    operator &&
                                    !operator.allowMultiple &&
                                    workingFilters
                                      .filter(i => i.id !== filter.id)
                                      .find(
                                        i =>
                                          i.definitionId === choice.id &&
                                          i.operatorId === operator.id,
                                      ),
                                  );
                                },
                              );
                              filterCopy.operatorId = validOperator
                                ? validOperator.id
                                : null;
                              filterCopy.value =
                                choice.operators[0].initialValue;
                            }
                            setWorkingFilter(filterCopy);
                          }}
                        />
                      </div>
                      <div className={styles.actionBarRowOperator}>
                        {filterDefinition ? (
                          <InputBox
                            type="select"
                            menuMaxHeight={300}
                            width="100%"
                            value={filter.operatorId}
                            choices={filterDefinition.operators.map(
                              operator => ({
                                ...operator,

                                // Disable operators that cannot be selected because the pair of
                                // filter definition and operator are unique together.
                                disabled: Boolean(
                                  operator &&
                                  !operator.allowMultiple &&
                                  workingFilters
                                    .filter(i => i.id !== filter.id)
                                    .find(
                                      i =>
                                        i.definitionId ===
                                        filterDefinition.id &&
                                        i.operatorId === operator.id,
                                    ),
                                ),
                              }),
                            )}
                            onChange={choice => (
                              setWorkingFilter({
                                ...filter,
                                operatorId: choice.id,
                                value: choice.initialValue,
                              })
                            )}
                          />
                        ) : (
                          // No filter definition selected
                          <InputBox type="text" disabled width="100%" />
                        )}
                      </div>
                      <div className={styles.actionBarRowDelete}>
                        <Button
                          onClick={() => {
                            const filtersCopy = workingFilters.slice();
                            filtersCopy.splice(index, 1);
                            setWorkingFilters(filtersCopy);
                          }}
                          variant="underline"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                    {operator ? (
                      <div className={styles.actionBarBottomRow}>
                        <operator.component
                          value={filter.value}
                          setValue={value =>
                            setWorkingFilter({ ...filter, value })
                          }
                          definition={filterDefinition}
                          operator={operator}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            <AppBarContext.Provider value="BOTTOM_ACTIONS">
              <AppBar>
                <AppBarSection />
                <AppBarSection>
                  <Button
                    variant="filled"
                    onClick={() => {
                      setWorkingFilters([
                        ...workingFilters,
                        Filter.create(null, null, null),
                      ]);
                    }}
                    leftIcon={<Icons.Plus />}
                  >
                    Add Filter
                  </Button>
                </AppBarSection>
              </AppBar>
            </AppBarContext.Provider>
          </Fragment>
        ) : (
          <div className={styles.actionBarEmptyState}>
            <h3>No filters are currently active.</h3>
            <Button
              onClick={() => {
                setWorkingFilters([
                  ...workingFilters,
                  Filter.create(null, null, null),
                ]);
              }}
              leftIcon={<Icons.Plus />}
              variant="filled"
            >
              Add Filter
            </Button>
          </div>
        )}
      </div>
    </Popup>
  );
};

type ItemListChoice = Choice & { disabled?: boolean };
type ItemListProps = {
  choices: Array<ItemListChoice>;
  selected?: ItemListChoice['id'];
  template?: (choice: ItemListChoice) => React.ReactNode;
  onClick: (
    choice: ItemListChoice,
    event: React.SyntheticEvent<HTMLLIElement>,
  ) => void;
  noPadding?: boolean;
};

const DEFAULT_TEMPLATE = choice => choice.label;
export const ItemList: React.FunctionComponent<ItemListProps> = function ItemList(
  props,
) {
  const {
    choices,
    selected = null,
    template = DEFAULT_TEMPLATE,
    onClick,
    noPadding,
  } = props;
  return (
    <ul
      className={classnames(styles.itemList, { [styles.noPadding]: noPadding })}
    >
      {choices.map(choice => (
        <li
          data-test-id={`item-list-${choice.id}-item`}
          tabIndex={!choice.disabled ? 0 : undefined}
          key={choice.id}
          className={classnames({
            [styles.disabled]: choice.disabled,
            [styles.selected]: selected === choice.id,
          })}
          onClick={e => {
            if (choice.disabled !== true) {
              onClick(choice, e);
            }
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLLIElement>) => {
            switch (e.key) {
              // Enter will select the current item
              case 'Enter':
              case ' ':
                if (choice.disabled !== true) {
                  e.preventDefault();
                  onClick(choice, e);
                }
                return;
              // Arrow keys will move up and down
              case 'ArrowUp':
                let previousElement = e.currentTarget.previousElementSibling;
                if (previousElement) {
                  // If the previous element is not selectable, then keep going backwards until a
                  // selectable element it found.
                  while (
                    previousElement &&
                    previousElement.getAttribute('tabIndex') !== '0'
                  ) {
                    previousElement = previousElement.previousElementSibling;
                  }
                  if (previousElement) {
                    e.preventDefault();
                    (previousElement as HTMLElement).focus();
                  }
                }
                return;
              case 'ArrowDown':
                let nextElement = e.currentTarget.nextElementSibling;
                if (nextElement) {
                  // If the next element is not selectable, then keep going backwards until a
                  // selectable element it found.
                  while (
                    nextElement &&
                    nextElement.getAttribute('tabIndex') !== '0'
                  ) {
                    nextElement = nextElement.nextElementSibling;
                  }
                  if (nextElement) {
                    e.preventDefault();
                    (nextElement as HTMLElement).focus();
                  }
                }
                return;
            }
          }}
        >
          {template(choice)}
        </li>
      ))}
    </ul>
  );
};

type MultipleSelectItemListProps = {
  choices: Array<ItemListChoice>;
  value: Array<ItemListChoice['id']>;
  onChange: (choices: Array<ItemListChoice['id']>) => void;
  noPadding?: boolean;
};

export const MultipleSelectItemList: React.FunctionComponent<MultipleSelectItemListProps> = ({
  choices,
  value,
  onChange,
  noPadding,
}) => (
  <ItemList
    choices={choices}
    template={choice => (
      <Fragment>
        <Checkbox
          id={choice.id as string}
          checked={value.includes(choice.id)}
          onChange={e => {
            if ((e.target as HTMLInputElement).checked) {
              onChange([...value, choice.id]);
            } else {
              onChange(value.filter(i => i !== choice.id));
            }
          }}
          color={colorVariables.midnight}
        />
        <span className={styles.multipleSelectListItem}>{choice.label}</span>
      </Fragment>
    )}
    onClick={(choice, e) => {
      const tagName = (e.target as HTMLElement).tagName.toLowerCase();
      if (tagName === 'input' || tagName === 'label') {
        return;
      }

      if (value.includes(choice.id)) {
        onChange(value.filter(i => i !== choice.id));
      } else {
        onChange([...value, choice.id]);
      }
    }}
    noPadding={noPadding}
  />
);

type SingleSelectItemListProps = {
  choices: Array<ItemListChoice>;
  value: ItemListChoice['id'];
  onChange: (choice: ItemListChoice['id']) => void;
  noPadding?: boolean;
};

export const SingleSelectItemList: React.FunctionComponent<SingleSelectItemListProps> = ({
  choices,
  value,
  onChange,
  noPadding,
}) => (
  <ItemList
    choices={choices}
    template={choice => (
      <Fragment>
        <RadioButton
          id={choice.id as string}
          checked={value === choice.id}
          onChange={e => onChange(choice.id)}
          color={colorVariables.midnight}
        />
        <span className={styles.multipleSelectListItem}>{choice.label}</span>
      </Fragment>
    )}
    onClick={(choice, e) => {
      const tagName = (e.target as HTMLElement).tagName.toLowerCase();
      if (tagName === 'input' || tagName === 'label') {
        return;
      }

      onChange(choice.id);
    }}
    noPadding={noPadding}
  />
);

export const ActionBarCount: React.FunctionComponent<{
  total: number;
  units: React.ReactNode;
}> = ({ total, units }) => (
  <span className={styles.total}>
    {units}: <span className={styles.totalNumber}>{total}</span>
  </span>
);

type ActionBarSearchProps = {
  searchValue?: string;
  searchPlaceholder?: string;
  searchWidth?: React.ReactText;
  onChangeSearch?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

export const ActionBarSearch: React.FunctionComponent<ActionBarSearchProps> = ({
  searchValue,
  onChangeSearch,
  searchPlaceholder,
  searchWidth,
}) => (
  <div className={styles.filterSearchWrapper}>
    <InputBox
      leftIcon={<Icons.Search />}
      value={searchValue}
      onChange={onChangeSearch}
      placeholder={searchPlaceholder || 'Search'}
      width={searchWidth || 300}
    />
  </div>
);

type ActionBarProps = {
  units: string;
  totalItemCount: number | null;
  selectedItemCount: number;
  onClearSelectedItems: () => void;
  selectedActionButtons?: React.ReactNode;

  showSearch?: boolean;
  searchValue?: string;
  searchPlaceholder?: string;
  searchWidth?: React.ReactText;
  onChangeSearch?: (e: React.ChangeEvent<HTMLInputElement>) => void;

  actionButtons?: React.ReactNode;

  showFilters?: boolean;

  showSelectTypeToggle?: boolean;

  editModeButtons?: React.ReactNode;
} & ActionBarFilterProps &
  ActionBarSearchProps &
  ActionBarSelectTypeToggleProps;

const ActionBar: React.FunctionComponent<ActionBarProps> = props => {
  const {
    units,
    totalItemCount,
    selectedItemCount,
    onClearSelectedItems,
    selectedActionButtons,

    showFilters = true,
    filterDefinitions,
    filters,
    onChangeFilters,

    showSearch = false,
    searchValue,
    searchPlaceholder,
    searchWidth,
    onChangeSearch,

    showSelectTypeToggle = false,
    isSingleSelect = true,
    onChangeSelectType,

    actionButtons,
    editModeButtons,
  } = props;

  return (
    <AppBar>
      <AppBarSection>
        {selectedItemCount > 0 ? (
          <Fragment>
            <ActionBarCount
              total={selectedItemCount}
              units={`${units} Selected`}
            />
            <div className={styles.clearSelectionWrapper}>
              <Button onClick={onClearSelectedItems}>Clear Selection</Button>
            </div>
          </Fragment>
        ) : totalItemCount !== null ? (
          <ActionBarCount total={totalItemCount} units={`Total ${units}`} />
        ) : (
          // "Removing" the count
          null
        )}
      </AppBarSection>
      <AppBarSection>
        {selectedActionButtons && selectedItemCount > 0 ? (
          selectedActionButtons
        ) : (
          <HorizontalForm>
            {actionButtons}

            {actionButtons ? (
              <div className={styles.actionBarActionButtonSeperator} />
            ) : null}

            {(showFilters && (!showSelectTypeToggle || isSingleSelect)) ? (
              <ActionBarFilters
                filterDefinitions={filterDefinitions}
                filters={filters}
                onChangeFilters={onChangeFilters}
                position={
                  showSearch
                    ? {
                      top: 44,
                      right: -1 * (((searchWidth as number) || 300) + 8),
                    }
                    : undefined
                }
              />
            ) : null}

            {(showSelectTypeToggle && !isSingleSelect) &&
              editModeButtons
            }

            {showSelectTypeToggle && (
              <ActionBarSelectTypeToggle
                isSingleSelect={isSingleSelect}
                onChangeSelectType={onChangeSelectType}
              />
            )}

            {/* optionally render a search box at the end of the filter bar */}
            {showSearch ? (
              <ActionBarSearch
                searchValue={searchValue}
                onChangeSearch={onChangeSearch}
                searchPlaceholder={searchPlaceholder}
                searchWidth={searchWidth}
              />
            ) : null}
          </HorizontalForm>
        )}
      </AppBarSection>
    </AppBar>
  );
};

export default ActionBar;
